import type React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { MentionCandidate } from "@/lib/humhub-api/types";
import { isStudyGroupCandidate } from "@/lib/utils/mentionUtils";

interface MentionPosition {
  top: number;
  left: number;
}

interface MentionSuggestionsProps {
  id?: string;
  isVisible: boolean;
  position: MentionPosition | null;
  candidates: MentionCandidate[];
  selectedIndex: number;
  isLoading: boolean;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  loadMoreError?: string | null;
  onFetchMore?: () => void;
  onSelect: (item: MentionCandidate) => void;
  onClose: () => void;
  suggestionsRef: React.RefObject<HTMLDivElement | null>;
}

export const MentionSuggestions: React.FC<MentionSuggestionsProps> = ({
  id = "mention-suggestions-listbox",
  isVisible,
  position,
  candidates,
  selectedIndex,
  isLoading,
  hasMore = false,
  isFetchingMore = false,
  loadMoreError,
  onFetchMore,
  onSelect,
  onClose,
  suggestionsRef,
}) => {
  const { t } = useTranslation();
  const suggestionCount = candidates.length + (hasMore ? 1 : 0);
  // キーボードショートカットの処理
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible) return;

      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, onClose]);

  // 選択されたアイテムを表示領域内にスクロール
  useEffect(() => {
    if (
      isVisible &&
      suggestionsRef.current &&
      selectedIndex < suggestionCount
    ) {
      const selectedElement = suggestionsRef.current.querySelector<HTMLElement>(
        `[data-mention-suggestion-index="${selectedIndex}"]`,
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedIndex, isVisible, suggestionCount, suggestionsRef]);

  if (!isVisible || !position) {
    return null;
  }

  const handleItemClick = (item: MentionCandidate) => {
    onSelect(item);
  };

  return (
    <div
      id={id}
      ref={suggestionsRef}
      role="listbox"
      className="fixed z-50 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
      style={{
        top: position.top,
        left: position.left,
        width: "300px",
      }}
    >
      {isLoading ? (
        <output className="block p-3 text-center text-gray-500">
          <div className="mr-2 inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent text-blue-600" />
          {t("common.loading")}
        </output>
      ) : (
        <div className="py-1">
          {candidates.length === 0 && (
            <div className="p-3 text-center text-gray-500">
              {t("common.noMatchesFound")}
            </div>
          )}
          {candidates.map((item, index) => {
            const isSelected = index === selectedIndex;
            const isGroup = isStudyGroupCandidate(item);

            return (
              <div
                key={item.id}
                id={`${id}-option-${index}`}
                role="option"
                aria-selected={isSelected}
                tabIndex={-1}
                data-mention-suggestion-index={index}
                className={`flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors ${
                  isSelected
                    ? isGroup
                      ? "bg-green-50 text-green-900"
                      : "bg-blue-50 text-blue-900"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => handleItemClick(item)}
              >
                <Avatar className="size-8 shrink-0">
                  <AvatarImage src={item.avatarUrl} alt={item.displayName} />
                  <AvatarFallback
                    className={
                      isGroup
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }
                  >
                    {item.displayName?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">
                      {item.displayName}
                    </span>
                  </div>
                  {item.secondaryText && (
                    <span className="truncate text-xs text-gray-500">
                      {item.secondaryText}
                    </span>
                  )}
                </div>

                <div className="shrink-0">
                  {isGroup ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                      {t("common.group")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                      {t("common.user")}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          {hasMore && (
            <button
              type="button"
              id={`${id}-option-${candidates.length}`}
              role="option"
              aria-selected={selectedIndex === candidates.length}
              tabIndex={-1}
              data-mention-suggestion-index={candidates.length}
              className={`w-full px-3 py-2 text-center text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 disabled:cursor-wait disabled:text-gray-500 ${
                selectedIndex === candidates.length ? "bg-blue-50" : ""
              }`}
              disabled={isFetchingMore}
              onMouseDown={(event) => event.preventDefault()}
              onClick={onFetchMore}
            >
              {isFetchingMore
                ? t("common.mentionLoadingMore")
                : loadMoreError
                  ? t("common.mentionLoadMoreRetry")
                  : t("common.showMore")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
